import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { buildCompanyProfileContext } from "./assistantPageContext";
import toast from "react-hot-toast";
import { openReportLoadingTab } from "../utils/reportPdfLoader";
import {
  createCompanyProfile,
  fetchAdminUsers,
  fetchCompanyProfiles,
  handoverCompanyOwnership,
  isSuperAdmin,
  toggleCompanyPause,
  updateCompanyProfile,
  fetchAssessmentFormats,
} from "../api/adminClient";
import {
  EXEC_SUMMARY_ROLES,
  fetchExecutiveSummaryStatus,
  fetchExecutiveSummaryStatusBatch,
  generateExecutiveSummaryPdf,
  previewExecutiveSummary,
} from "../api/reportProfilesClient";
import { isAbortError } from "../utils/fetchAbort";
import {
  EBITDA_OPTIONS,
  FILTER_ALL_OWNERSHIP,
  REVENUE_OPTIONS,
  WORKSPACE_COMPANY_SIZES,
  WORKSPACE_INDUSTRIES,
  WORKSPACE_OWNERSHIP,
} from "./companyWorkspaceOptions";
import { uploadCompanyLogo } from "./uploadCompanyLogo";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const PAGE_SIZE_STORAGE_KEY = "company-profile-page-size";

const inputClass =
  "w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#004f73]/20 focus:border-[#004f73]/30";
const labelClass = "block text-sm font-medium text-slate-600 mb-1";

function Spinner({ className = "h-5 w-5" }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

function CeoPillarChartPanel({ sec, compact = false }) {
  return (
    <div className={`border border-gray-200 rounded-lg bg-gray-50/80 w-full ${compact ? "p-2.5" : "p-2.5"}`}>
      <p className={`font-bold uppercase tracking-wide text-[#004f73] mb-1.5 ${compact ? "text-[10px]" : "text-[9px]"}`}>{sec.title}</p>
      <div className={compact ? "space-y-2" : "space-y-1"}>
        {(sec.bars || []).map((bar) => (
          <div key={bar.label} className={`flex items-center gap-1.5 ${compact ? "text-[10px]" : "text-[8px]"}`}>
            <span className="w-[38%] truncate text-gray-700">{bar.label}</span>
            <div className={`flex-1 bg-gray-200 rounded overflow-hidden ${compact ? "h-2.5" : "h-1.5"}`}>
              <div
                className="h-full rounded"
                style={{ width: `${bar.pct}%`, backgroundColor: bar.color || "#004f73" }}
              />
            </div>
            <span className="w-7 text-right font-semibold text-[#004f73]">{bar.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CeoGapChartPanel({ sec, compact = false }) {
  const impScale = sec.importance_scale_max || sec.scale_max || 9;
  const agrScale = sec.agreement_scale_max || sec.scale_max || 5;
  return (
    <div className={`border border-gray-200 rounded-lg bg-gray-50/80 w-full p-2.5`}>
      <p className={`font-bold uppercase tracking-wide text-[#004f73] mb-0.5 ${compact ? "text-[10px]" : "text-[9px]"}`}>{sec.title}</p>
      <div className={compact ? "space-y-2.5" : "space-y-1.5"}>
        {(sec.entries || []).map((row) => (
          <div key={row.label}>
            <p className={`font-semibold text-gray-800 leading-snug ${compact ? "text-[10px]" : "text-[8px]"}`}>
              {row.label}
              {row.pillar_pct != null ? (
                <span className="font-normal text-gray-500">
                  {" "}
                  ({row.pillar_pct}% · Δ{row.gap})
                </span>
              ) : null}
            </p>
            {[
              ["Imp.", row.importance, impScale, "#004f73"],
              ["Agr.", row.agreement, agrScale, "#7eb8d4"],
            ].map(([label, val, scale, color]) => (
              <div key={label} className={`flex items-center gap-1 mt-0.5 ${compact ? "text-[9px]" : "text-[7px]"}`}>
                <span className="w-6 text-gray-500">{label}</span>
                <div className={`flex-1 bg-gray-200 rounded overflow-hidden ${compact ? "h-2" : "h-1"}`}>
                  <div
                    className="h-full rounded"
                    style={{ width: `${(val / scale) * 100}%`, backgroundColor: color }}
                  />
                </div>
                <span className="w-4 text-right">{val}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
      <p className={`text-gray-500 mt-1.5 ${compact ? "text-[8px]" : "text-[7px]"}`}>
        <span className="inline-block w-2 h-2 rounded-sm bg-[#004f73] mr-1 align-middle" />
        Importance
        <span className="inline-block w-2 h-2 rounded-sm bg-[#7eb8d4] mx-1 align-middle" />
        Agreement
      </p>
    </div>
  );
}

function BriefingChartPanel({ sec, compact = false }) {
  if (sec?.type === "gap_chart") return <CeoGapChartPanel sec={sec} compact={compact} />;
  if (sec?.type === "pillar_chart") return <CeoPillarChartPanel sec={sec} compact={compact} />;
  return null;
}

function DeliversBreaksPanels({ delivers, breaks, headSize, stacked = false }) {
  if (!delivers && !breaks) return null;
  const panelClass = "border border-gray-200 rounded-lg bg-white/80 p-2.5";
  const wrapClass = stacked ? "space-y-2" : "grid grid-cols-2 gap-2";
  return (
    <div className={wrapClass}>
      {delivers ? (
        <div className={panelClass}>
          <p className={`font-bold uppercase tracking-wide text-[#004f73] mb-1 ${headSize}`}>{delivers.title}</p>
          <ul className="list-disc pl-4 space-y-0.5">
            {(delivers.entries || []).map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {breaks ? (
        <div className={panelClass}>
          <p className={`font-bold uppercase tracking-wide text-[#004f73] mb-1 ${headSize}`}>{breaks.title}</p>
          <ul className="list-disc pl-4 space-y-0.5">
            {(breaks.entries || []).map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function StructuredBriefingPreview({ preview }) {
  const sections = preview.briefing_sections || [];
  const chartSection = sections.find((s) => s.type === "gap_chart" || s.type === "pillar_chart");
  const delivers = sections.find((s) => s.id === "delivers");
  const breaks = sections.find((s) => s.id === "breaks");
  const isPillarLayout = preview.layout === "pe_structured" || preview.layout === "board_structured";
  const textSize = isPillarLayout ? "text-[11px]" : "text-[10px]";
  const headSize = isPillarLayout ? "text-[10px]" : "text-[9px]";

  return (
    <div className={`space-y-3 ${textSize} text-gray-700`}>
      {sections.map((sec) => {
        if (sec.type === "pillar_chart" || sec.type === "gap_chart") return null;
        if (sec.id === "delivers" || sec.id === "breaks") return null;
        if (sec.id === "verdict") {
          return (
            <p key={sec.id} className={`font-semibold text-[#004f73] leading-snug ${isPillarLayout ? "text-[12px]" : ""}`}>
              {sec.content}
            </p>
          );
        }
        if (sec.type === "text") {
          const pillarRow =
            sec.id === "situation" && isPillarLayout && chartSection ? (
              <div className="mt-2 space-y-2">
                <BriefingChartPanel sec={chartSection} compact />
                <DeliversBreaksPanels delivers={delivers} breaks={breaks} headSize={headSize} />
              </div>
            ) : null;
          const gapChartRow =
            sec.id === "situation" && !isPillarLayout && chartSection ? (
              <div className="mt-2 w-full">
                <BriefingChartPanel sec={chartSection} compact />
              </div>
            ) : null;
          const gapBulletsRow =
            sec.id === "situation" && !isPillarLayout && (delivers || breaks) ? (
              <div className="mt-2">
                <DeliversBreaksPanels delivers={delivers} breaks={breaks} headSize={headSize} />
              </div>
            ) : null;
          return (
            <div key={sec.id}>
              <p className={`font-bold uppercase tracking-wide text-[#004f73] ${headSize}`}>{sec.title}</p>
              <p className="leading-relaxed mt-0.5">{sec.content}</p>
              {pillarRow}
              {gapChartRow}
              {gapBulletsRow}
            </div>
          );
        }
        if (sec.type === "callout") {
          return (
            <div key={sec.id} className={`border-l-2 border-[#004f73] bg-[#f0f7fa] pl-2 italic ${isPillarLayout ? "py-2" : "py-1"}`}>
              <p className={`font-bold uppercase tracking-wide text-[#004f73] not-italic ${headSize}`}>{sec.title}</p>
              <p className="mt-0.5">{sec.content}</p>
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}

function ExecSummaryPreviewSections({ preview }) {
  if (preview?.layout?.endsWith("_structured") && preview?.briefing_sections?.length) {
    return <StructuredBriefingPreview preview={preview} />;
  }

  const sections = preview?.insight_sections;
  if (!sections?.length) return null;

  return (
    <div className="space-y-3">
      {sections.map((sec) => (
        <div key={sec.id}>
          <p className="text-[10px] font-bold uppercase tracking-wide text-[#004f73]">{sec.title}</p>
          {sec.description ? (
            <p className="text-[10px] text-gray-500 italic mb-1">{sec.description}</p>
          ) : null}
          <ul className="list-disc pl-4 text-[10px] text-gray-700 space-y-0.5">
            {(sec.bullets || []).map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function getPageNumbers(current, total) {
  if (total <= 1) return [0];
  if (total <= 7) return Array.from({ length: total }, (_, i) => i);
  const pages = new Set([0, total - 1, current, current - 1, current + 1]);
  return [...pages].filter((p) => p >= 0 && p < total).sort((a, b) => a - b);
}

function sortRows(rows, sortKey, sortDir) {
  if (!sortKey) return rows;
  const mul = sortDir === "desc" ? -1 : 1;
  return [...rows].sort((a, b) => {
    let av = a[sortKey];
    let bv = b[sortKey];
    if (sortKey === "survey") {
      av = a.is_paused === "Y" ? "paused" : "active";
      bv = b.is_paused === "Y" ? "paused" : "active";
    }
    if (sortKey === "company_ownership") {
      av = a.company_ownership || "";
      bv = b.company_ownership || "";
    }
    if (sortKey === "invited_by") {
      av = a.invited_by?.name || a.invited_by?.email || "";
      bv = b.invited_by?.name || b.invited_by?.email || "";
    }
    if (sortKey === "assigned_owner") {
      av = a.assigned_owner?.name || a.assigned_owner?.email || "";
      bv = b.assigned_owner?.name || b.assigned_owner?.email || "";
    }
    const as = (av ?? "").toString().toLowerCase();
    const bs = (bv ?? "").toString().toLowerCase();
    if (as < bs) return -1 * mul;
    if (as > bs) return 1 * mul;
    return 0;
  });
}

function adminDisplayName(admin) {
  if (!admin) return null;
  return admin.name || admin.email || null;
}

function IconSearch({ className = "w-4 h-4" }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden>
      <path
        fillRule="evenodd"
        d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
        clipRule="evenodd"
      />
    </svg>
  );
}

const emptyForm = () => ({
  company_name: "",
  year_of_foundation: "",
  revenue_last_year: "",
  revenue_last_12months: "",
  industry: "",
  company_size: "",
  ebita: "",
  company_logo: "",
  company_ownership: "",
  assessment_format_id: "",
});

function FormSection({ title, children }) {
  return (
    <section className="space-y-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">{title}</h3>
      {children}
    </section>
  );
}

function LogoUploadBlock({ previewUrl, onPickFile, uploading, inputId = "company-logo-input" }) {
  return (
    <div className="flex items-center gap-4">
      <label htmlFor={inputId} className="cursor-pointer shrink-0">
        <div className="h-20 w-20 rounded-xl bg-slate-100 flex items-center justify-center overflow-hidden border border-slate-200">
          {previewUrl ? (
            <img src={previewUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-[10px] text-slate-400 text-center px-2">PNG, JPG</span>
          )}
        </div>
      </label>
      <div>
        <p className="text-sm font-medium text-slate-700">Company logo</p>
        <p className="text-xs text-slate-500 mt-0.5">Optional — shown in the company list</p>
        <button
          type="button"
          className="mt-2 inline-flex items-center px-3 py-1.5 text-xs font-medium text-[#004f73] bg-[#004f73]/5 hover:bg-[#004f73]/10 rounded-lg border border-[#004f73]/20 transition-colors disabled:opacity-50"
          disabled={uploading}
          onClick={() => document.getElementById(inputId)?.click()}
        >
          {uploading ? "Uploading…" : "Upload logo"}
        </button>
      </div>
      <input
        id={inputId}
        type="file"
        className="hidden"
        accept=".png,.jpg,.jpeg,image/png,image/jpeg"
        onChange={onPickFile}
      />
    </div>
  );
}

function CompanyFormFields({
  form,
  setForm,
  errors,
  assessmentFormats = [],
  activeFormat = null,
}) {
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <div className="space-y-6">
      <FormSection title="Identity">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className={labelClass}>
              Company name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.company_name}
              onChange={set("company_name")}
              className={`${inputClass} ${errors.company_name ? "border-red-400" : ""}`}
              placeholder="e.g. Advantage CEO"
            />
            {errors.company_name ? (
              <p className="text-red-600 text-xs mt-1">{errors.company_name}</p>
            ) : null}
          </div>
          <div>
            <label className={labelClass}>Year of foundation</label>
            <input
              type="number"
              value={form.year_of_foundation}
              onChange={set("year_of_foundation")}
              className={inputClass}
              placeholder="e.g. 2010"
            />
          </div>
          <div>
            <label className={labelClass}>Industry</label>
            <select value={form.industry} onChange={set("industry")} className={inputClass}>
              <option value="">Select industry</option>
              {WORKSPACE_INDUSTRIES.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
        </div>
      </FormSection>

      <FormSection title="Assessment">
        <div>
          <label className={labelClass}>Assessment format</label>
          <select
            value={form.assessment_format_id || ""}
            onChange={set("assessment_format_id")}
            className={inputClass}
          >
            <option value="">
              {activeFormat?.name
                ? `Active assessment (${activeFormat.name})`
                : "Active assessment"}
            </option>
            {assessmentFormats
              .filter((f) => {
                if (!f.is_system) return true;
                if (f.id === form.assessment_format_id) return true;
                const activeIsCustom = assessmentFormats.some(
                  (other) => !other.is_system && other.id === activeFormat?.id
                );
                return !activeIsCustom;
              })
              .map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                  {f.is_system ? " (system)" : ""}
                </option>
              ))}
          </select>
          <p className="text-xs text-slate-500 mt-1">
            The active assessment is used for every company. Choose a format here only to
            override this company.
          </p>
        </div>
      </FormSection>

      <FormSection title="Financials">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Revenue (current fiscal year)</label>
            <select value={form.revenue_last_year} onChange={set("revenue_last_year")} className={inputClass}>
              <option value="">Select range</option>
              {REVENUE_OPTIONS.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Revenue (last 12 months)</label>
            <select
              value={form.revenue_last_12months}
              onChange={set("revenue_last_12months")}
              className={inputClass}
            >
              <option value="">Select range</option>
              {REVENUE_OPTIONS.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>EBITDA (current fiscal year)</label>
            <select value={form.ebita} onChange={set("ebita")} className={inputClass}>
              <option value="">Select range</option>
              {EBITDA_OPTIONS.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
        </div>
      </FormSection>

      <FormSection title="Classification">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Company ownership type</label>
            <select
              value={form.company_ownership}
              onChange={set("company_ownership")}
              className={inputClass}
            >
              <option value="">Select ownership type</option>
              {WORKSPACE_OWNERSHIP.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>No. of employees (FTE)</label>
            <select value={form.company_size} onChange={set("company_size")} className={inputClass}>
              <option value="">Select range</option>
              {WORKSPACE_COMPANY_SIZES.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
        </div>
      </FormSection>
    </div>
  );
}

function ModalBackdrop({ children, onClose, variant = "default" }) {
  const isPanel = variant === "panel";
  return (
    <div
      className="admin-modal-backdrop fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className={
          isPanel
            ? "admin-modal-card bg-white w-full max-w-4xl rounded-xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col"
            : "admin-modal-card bg-white w-full max-w-3xl rounded-xl shadow-xl max-h-[90vh] overflow-y-auto p-6 relative"
        }
        onClick={(e) => e.stopPropagation()}
      >
        {!isPanel ? (
          <button
            type="button"
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-xl z-10"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        ) : null}
        {children}
      </div>
    </div>
  );
}

function IconRefresh({ className = "w-4 h-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
      />
    </svg>
  );
}

function IconFileBrief({ className = "h-4 w-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function IconEye({ className = "h-4 w-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );
}

function IconDownload({ className = "h-4 w-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
  );
}

function IconChevronRight({ className = "h-4 w-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

function IconChevronDown({ className = "h-4 w-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
    </svg>
  );
}

function TablePortalSelect({
  value,
  options,
  onChange,
  placeholder = "Unassigned",
  ariaLabel,
  compact = false,
}) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const selected = options.find((opt) => opt.value === value);
  const displayLabel = selected?.label || placeholder;

  const updateMenuPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const menuMaxHeight = 240;
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const openUp = spaceBelow < 180 && spaceAbove > spaceBelow;
    const maxHeight = Math.min(menuMaxHeight, openUp ? spaceAbove : spaceBelow);

    setMenuStyle({
      position: "fixed",
      left: rect.left,
      top: openUp ? rect.top - maxHeight - 4 : rect.bottom + 4,
      width: Math.max(rect.width, 200),
      maxHeight,
      zIndex: 9999,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updateMenuPosition();
    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      const target = event.target;
      if (
        triggerRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const menu =
    open && menuStyle
      ? createPortal(
          <div
            ref={menuRef}
            role="listbox"
            aria-label={ariaLabel}
            className="overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-xl ring-1 ring-black/5"
            style={menuStyle}
          >
            {options.map((opt) => {
              const isSelected = opt.value === value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`flex w-full items-center px-3 py-2 text-left text-xs transition-colors ${
                    isSelected
                      ? "bg-[#004f73]/8 text-[#004f73] font-medium"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                  onClick={() => {
                    setOpen(false);
                    if (opt.value !== value) onChange(opt.value);
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        className={`flex w-full items-center justify-between gap-1.5 rounded-md border border-slate-200 bg-white text-left text-slate-800 shadow-sm transition-colors hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#004f73]/20 ${
          compact
            ? "min-w-[140px] px-2 py-1 text-[11px] leading-tight"
            : "min-w-[168px] px-2.5 py-2 text-xs"
        }`}
      >
        <span className="truncate">{displayLabel}</span>
        <IconChevronDown
          className={`h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {menu}
    </>
  );
}

const EXEC_ROLE_META = {
  CEO: { abbr: "CEO", tone: "bg-[#004f73]" },
  "Leadership Team": { abbr: "LT", tone: "bg-[#006994]" },
  "PE Sponsor": { abbr: "PE", tone: "bg-[#00838f]" },
  "Board Member": { abbr: "BD", tone: "bg-[#455a64]" },
};

function execSummaryKey(orgId, roleId) {
  return `${orgId}::${roleId}`;
}

function ExecSummaryStatusBadge({ status, compact = false }) {
  if (status?.loading) {
    return (
      <span className={`inline-flex items-center gap-1 font-medium text-gray-500 ${compact ? "text-[10px]" : "text-xs"}`}>
        <Spinner className="h-3 w-3" />
        Checking…
      </span>
    );
  }
  if (status?.ready) {
    return (
      <span
        className={`inline-flex items-center gap-1 font-medium text-green-800 bg-green-50 border border-green-200 rounded-full ${
          compact ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1"
        }`}
      >
        <span className="h-1.5 w-1.5 rounded-full bg-green-500 shrink-0" />
        Ready
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center gap-1 font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-full ${
        compact ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1"
      }`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
      No surveys
    </span>
  );
}

function ExecutiveSummaryPill({ status, onOpenSummaries }) {
  if (status?.loading) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-slate-200 bg-slate-50 px-2 py-1 text-[11px] font-medium leading-none text-slate-400"
        title="Checking executive summary status…"
      >
        <Spinner className="h-3 w-3" />
        Checking…
      </span>
    );
  }

  const ready = Boolean(status?.ready);
  const memberCount = status?.num_members || 0;
  const memberHint =
    memberCount > 0
      ? `${memberCount} completed response${memberCount === 1 ? "" : "s"}`
      : undefined;

  if (!ready) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-slate-200 bg-slate-50/80 px-2 py-1 text-[11px] font-medium leading-none text-slate-400 opacity-70"
        title="No completed surveys — executive summary unlocks when the team finishes the assessment"
      >
        <IconFileBrief className="h-3 w-3 shrink-0 text-slate-400" />
        Unavailable
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={onOpenSummaries}
      title={memberHint ? `Open executive summary · ${memberHint}` : "Open executive summary"}
      className="exec-summary-btn group inline-flex items-center gap-1.5 rounded-md bg-[#004f73] px-2.5 py-1.5 text-[11px] font-semibold leading-none text-white shadow-sm transition-colors hover:bg-[#003a57] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#004f73]/50 focus-visible:ring-offset-1"
    >
      <IconFileBrief className="h-3 w-3 shrink-0" />
      View summary
      <IconChevronRight className="h-3 w-3 shrink-0 opacity-80 transition-transform group-hover:translate-x-0.5" />
    </button>
  );
}

function CompanyLogoAvatar({ row }) {
  const name = row.company_name || "?";
  if (row.company_logo) {
    return (
      <img
        src={row.company_logo}
        alt=""
        className="h-7 w-7 rounded-md object-cover border border-slate-200 shrink-0"
      />
    );
  }
  return (
    <div className="h-7 w-7 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0 text-[9px] font-semibold text-slate-500">
      {name.slice(0, 2).toUpperCase()}
    </div>
  );
}

function CompanyCell({ row, onEdit }) {
  const meta = [
    row.industry,
    row.year_of_foundation ? `Founded ${row.year_of_foundation}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="flex items-center gap-2 min-w-[180px]">
      <CompanyLogoAvatar row={row} />
      <div className="min-w-0 leading-tight">
        <button
          type="button"
          onClick={() => onEdit(row)}
          className="font-semibold text-slate-900 hover:text-[#004f73] text-left truncate block max-w-full text-xs transition-colors"
        >
          {row.company_name || "—"}
        </button>
        <p className="text-[11px] text-slate-500 truncate leading-none mt-0.5">{meta || "No industry set"}</p>
      </div>
    </div>
  );
}

function FinancialsCell({ row }) {
  const fy = row.revenue_last_year;
  const tm = row.revenue_last_12months;
  const ebita = row.ebita;
  const employees = row.company_size;

  if (!fy && !tm && !ebita && !employees) {
    return <span className="text-[11px] leading-none text-slate-400">Not set</span>;
  }

  let revenueLine = null;
  if (fy && tm && fy === tm) revenueLine = fy;
  else if (fy || tm) {
    revenueLine = [fy && `FY ${fy}`, tm && `12mo ${tm}`].filter(Boolean).join(" · ");
  }

  return (
    <div className="text-[11px] text-slate-700 leading-tight space-y-0.5 min-w-[130px]">
      {employees ? (
        <p className="truncate">
          <span className="text-slate-400">FTE </span>
          {employees}
        </p>
      ) : null}
      {revenueLine ? (
        <p className="truncate">
          <span className="text-slate-400">Revenue </span>
          {revenueLine}
        </p>
      ) : null}
      {ebita ? (
        <p className="truncate">
          <span className="text-slate-400">EBITDA </span>
          {ebita}
        </p>
      ) : null}
    </div>
  );
}

function OwnershipCell({ row, admins, canHandover, ownerSelectValue, onOwnerChange }) {
  const invitedLabel = adminDisplayName(row.invited_by) || "Unknown";
  const memberLabel = adminDisplayName(row.assigned_owner) || "Unassigned";
  const memberOptions = admins.map((admin) => ({
    value: admin.id,
    label: adminDisplayName(admin) || admin.email,
  }));

  return (
    <div className="text-[11px] text-slate-700 leading-tight min-w-[152px] space-y-1">
      {row.company_ownership ? (
        <p className="truncate">
          <span className="text-slate-400">Type </span>
          {row.company_ownership}
        </p>
      ) : null}
      <p className="truncate text-slate-800 font-medium">{invitedLabel}</p>
      <div className="flex flex-col gap-1">
        <span className="text-[10px] leading-none text-slate-400">Additional ownership</span>
        {canHandover ? (
          <TablePortalSelect
            compact
            value={ownerSelectValue}
            options={memberOptions}
            placeholder="Unassigned"
            ariaLabel={`Assign additional ownership for ${row.company_name}`}
            onChange={(nextId) => onOwnerChange(row, nextId)}
          />
        ) : (
          <span className="truncate text-slate-800">{memberLabel}</span>
        )}
      </div>
    </div>
  );
}

function SurveyStatusPill({ row, onClick }) {
  const active = row.is_paused !== "Y";
  return (
    <button
      type="button"
      onClick={onClick}
      title="Survey status & history"
      className={`inline-flex items-center gap-1 text-[11px] font-medium leading-none rounded-full px-2 py-0.5 border transition-colors hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#004f73]/40 ${
        active
          ? "text-green-800 bg-green-50 border-green-200"
          : "text-slate-600 bg-slate-100 border-slate-200"
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${active ? "bg-green-500" : "bg-slate-400"}`} />
      {active ? "Active" : "Paused"}
    </button>
  );
}

function ExecutiveSummariesModal({
  row,
  status,
  execPreviews,
  execPreviewLoading,
  execPdfLoading,
  onClose,
  onPreview,
  onDownload,
}) {
  const [selectedRoleId, setSelectedRoleId] = useState(EXEC_SUMMARY_ROLES[0].id);
  const disabled = status?.loading || status?.ready !== true;
  const selectedRole = EXEC_SUMMARY_ROLES.find((r) => r.id === selectedRoleId) || EXEC_SUMMARY_ROLES[0];
  const key = execSummaryKey(row._id, selectedRole.id);
  const preview = execPreviews[key];
  const previewBusy = Boolean(execPreviewLoading[key]);
  const pdfBusy = Boolean(execPdfLoading[key]);
  const meta = EXEC_ROLE_META[selectedRole.id] || { abbr: "?", tone: "bg-[#004f73]" };

  const handleRoleSelect = (roleId) => {
    setSelectedRoleId(roleId);
    const roleKey = execSummaryKey(row._id, roleId);
    if (!execPreviews[roleKey] && !execPreviewLoading[roleKey] && !disabled) {
      onPreview(roleId);
    }
  };

  return (
    <ModalBackdrop onClose={onClose} variant="panel">
      <div className="bg-gradient-to-r from-[#004f73] to-[#006994] px-5 py-4 text-white shrink-0 relative">
        <button
          type="button"
          className="absolute top-3 right-3 text-white/70 hover:text-white text-2xl leading-none"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-white/75 mb-1">
          Executive summary
        </p>
        <h2 className="text-xl font-bold pr-8">{row.company_name}</h2>
        <p className="text-sm text-white/80 mt-1 max-w-xl">
          Review role-based briefings and switch perspectives below.
        </p>
      </div>

      <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/80 shrink-0">
        {status?.loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Spinner className="h-4 w-4 text-[#004f73]" />
            Checking survey data…
          </div>
        ) : status?.ready === false ? (
          <div className="flex items-start gap-2 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <span className="mt-0.5 text-amber-600">⚠</span>
            <span>
              No completed surveys yet. Summaries unlock once team members finish the assessment.
            </span>
          </div>
        ) : status?.ready ? (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <ExecSummaryStatusBadge status={status} />
            <span className="text-xs text-gray-500">
              {status.num_members} completed response{status.num_members === 1 ? "" : "s"}
            </span>
          </div>
        ) : null}
      </div>

      <div className="flex flex-col sm:flex-row flex-1 min-h-0 overflow-hidden exec-summary-modal-body">
        <nav
          className="exec-summary-role-nav w-full sm:w-48 shrink-0 border-r border-gray-100 bg-gray-50/40 p-3 space-y-2 overflow-y-auto sm:overflow-y-auto flex sm:flex-col flex-row"
          aria-label="Summary perspectives"
        >
          <p className="hidden sm:block text-[10px] font-semibold uppercase tracking-wide text-slate-400 px-1">
            Select perspective
          </p>
          {EXEC_SUMMARY_ROLES.map((role) => {
            const roleKey = execSummaryKey(row._id, role.id);
            const rolePreview = execPreviews[roleKey];
            const roleLoading = Boolean(execPreviewLoading[roleKey]);
            const isActive = role.id === selectedRoleId;
            const roleMeta = EXEC_ROLE_META[role.id] || { abbr: "?", tone: "bg-[#004f73]" };
            return (
              <button
                key={role.id}
                type="button"
                onClick={() => handleRoleSelect(role.id)}
                className={`w-full rounded-lg px-2.5 py-2.5 text-left transition-colors ${
                  isActive
                    ? "bg-white shadow-sm border border-[#004f73]/25 ring-1 ring-[#004f73]/10"
                    : "hover:bg-white/70 border border-transparent"
                }`}
              >
                <span className="flex items-center gap-2.5">
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-[10px] font-bold text-white ${roleMeta.tone}`}
                  >
                    {roleMeta.abbr}
                  </span>
                  <span className="min-w-0">
                    <span className={`block text-xs font-semibold truncate ${isActive ? "text-[#004f73]" : "text-gray-800"}`}>
                      {role.label}
                    </span>
                    {roleLoading ? (
                      <span className="text-[10px] text-gray-400">Loading…</span>
                    ) : rolePreview?.overall_score != null ? (
                      <span className="text-[10px] text-gray-500">{rolePreview.overall_score}% overall</span>
                    ) : (
                      <span className="text-[10px] text-gray-400">Tap to load</span>
                    )}
                  </span>
                </span>
              </button>
            );
          })}
        </nav>

        <div className="flex-1 min-w-0 overflow-y-auto p-4 sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className={`flex h-8 w-8 items-center justify-center rounded-lg text-xs font-bold text-white ${meta.tone}`}>
                  {meta.abbr}
                </span>
                <h3 className="text-base font-bold text-gray-900">{selectedRole.label}</h3>
                {preview?.overall_score != null ? (
                  <span className="text-xs font-bold text-white bg-[#004f73] px-2 py-1 rounded-md">
                    {preview.overall_score}%
                  </span>
                ) : null}
              </div>
              <p className="text-sm text-gray-500 max-w-lg">{selectedRole.description}</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 text-sm px-3 py-2 white_btn disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={disabled || previewBusy}
              onClick={() => onPreview(selectedRole.id)}
            >
              {previewBusy ? <Spinner className="h-4 w-4" /> : <IconEye className="h-4 w-4" />}
              {previewBusy ? "Loading…" : preview ? "Refresh preview" : "Preview"}
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 text-sm px-3 py-2 blue_btn disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={disabled || pdfBusy}
              onClick={() => onDownload(selectedRole.id)}
            >
              {pdfBusy ? <Spinner className="h-4 w-4" /> : <IconDownload className="h-4 w-4" />}
              {pdfBusy ? "Generating…" : "Download PDF"}
            </button>
          </div>

          {previewBusy && !preview ? (
            <div className="rounded-xl border border-gray-200 bg-gray-50/50 px-6 py-12 text-center">
              <Spinner className="h-6 w-6 text-[#004f73] mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-700">Loading {selectedRole.label} summary…</p>
            </div>
          ) : preview ? (
            <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4">
              {preview.summary_source === "ai" ? (
                <span className="inline-block text-[10px] font-semibold text-[#004f73] bg-[#e8f4f8] px-2 py-0.5 rounded-full mb-3">
                  AI generated
                </span>
              ) : null}
              <div className="text-xs text-gray-700 leading-relaxed max-h-[420px] overflow-y-auto pr-1">
                {preview.layout?.endsWith("_structured") ? (
                  <ExecSummaryPreviewSections preview={preview} />
                ) : (
                  <>
                    <p className="mb-3 text-sm">{preview.overview}</p>
                    <div className="grid sm:grid-cols-3 gap-2 mb-3">
                      {[
                        ["Strengths", preview.strengths],
                        ["Risks", preview.risks],
                        ["Actions", preview.actions],
                      ].map(([label, items]) => (
                        <div key={label} className="bg-white border border-gray-200 rounded-lg p-2.5">
                          <p className="text-[10px] font-bold text-[#004f73] uppercase mb-1.5">{label}</p>
                          <ul className="list-disc pl-3.5 space-y-0.5 text-[11px] text-gray-700">
                            {(items || []).map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                    <ExecSummaryPreviewSections preview={preview} />
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/40 px-6 py-10 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#004f73]/10 text-[#004f73]">
                <IconEye className="h-6 w-6" />
              </div>
              <p className="text-sm font-medium text-gray-700 mb-1">No preview loaded</p>
              <p className="text-xs text-gray-500 mb-4 max-w-xs mx-auto">
                Preview the {selectedRole.label.toLowerCase()} briefing here, or download the one-page PDF directly.
              </p>
              <button
                type="button"
                className="text-sm white_btn disabled:opacity-50"
                disabled={disabled || previewBusy}
                onClick={() => onPreview(selectedRole.id)}
              >
                {previewBusy ? "Loading…" : "Load preview"}
              </button>
            </div>
          )}
        </div>
      </div>
    </ModalBackdrop>
  );
}

export default function CompanyProfilePage({ embedded = false, onCompanySaved }) {
  const { setAdminAssistantContext } = useOutletContext() || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [filterSurvey, setFilterSurvey] = useState("all");
  const [filterIndustry, setFilterIndustry] = useState("");
  const [filterOwnershipType, setFilterOwnershipType] = useState(FILTER_ALL_OWNERSHIP);
  const [filterAssignedOwner, setFilterAssignedOwner] = useState("");
  const [filterSummary, setFilterSummary] = useState("all");
  const [sortKey, setSortKey] = useState("company_name");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(() => {
    try {
      const saved = parseInt(localStorage.getItem(PAGE_SIZE_STORAGE_KEY) || "10", 10);
      return PAGE_SIZE_OPTIONS.includes(saved) ? saved : 10;
    } catch {
      return 10;
    }
  });
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editOrgId, setEditOrgId] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [assessmentFormats, setAssessmentFormats] = useState([]);
  const [activeFormat, setActiveFormat] = useState(null);

  useEffect(() => {
    if (searchParams.get("add_company") !== "1") return;
    setAddOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete("add_company");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const [sliderRow, setSliderRow] = useState(null);
  const [pauseConfirmOpen, setPauseConfirmOpen] = useState(false);
  const [pauseBusy, setPauseBusy] = useState(false);

  const [execPreviews, setExecPreviews] = useState({});
  const [execPreviewLoading, setExecPreviewLoading] = useState({});
  const [execPdfLoading, setExecPdfLoading] = useState({});
  const [execStatusByOrg, setExecStatusByOrg] = useState({});
  const [execSummariesModal, setExecSummariesModal] = useState(null);

  const superAdmin = isSuperAdmin();
  const [adminUsers, setAdminUsers] = useState([]);
  const [handoverConfirm, setHandoverConfirm] = useState(null);
  const [handoverBusy, setHandoverBusy] = useState(false);
  const [editOwnershipHistory, setEditOwnershipHistory] = useState([]);

  const load = useCallback(async ({ silent = false, signal } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await fetchCompanyProfiles({ signal });
      setCompanies(res.data?.companies || []);
      setActiveFormat(res.data?.active_format || null);
    } catch (e) {
      if (isAbortError(e)) return;
      toast.error(e.message || "Could not load companies");
    } finally {
      if (!silent && !signal?.aborted) setLoading(false);
    }
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await load({ silent: true });
      toast.success("List refreshed");
    } catch {
      /* load shows toast */
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    load({ signal: controller.signal });
    return () => controller.abort();
  }, [load]);

  useEffect(() => {
    fetchAssessmentFormats()
      .then((res) => setAssessmentFormats(res.data?.formats || []))
      .catch(() => setAssessmentFormats([]));
  }, []);

  useEffect(() => {
    if (!superAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchAdminUsers();
        if (!cancelled) setAdminUsers(res.data?.users || []);
      } catch {
        if (!cancelled) setAdminUsers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [superAdmin]);

  useEffect(() => {
    if (!companies.length) {
      setExecStatusByOrg({});
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;
    setExecStatusByOrg(Object.fromEntries(companies.map((row) => [row._id, { loading: true }])));

    (async () => {
      try {
        const res = await fetchExecutiveSummaryStatusBatch(
          companies.map((row) => row.company_name),
          { signal }
        );
        const byName = res.data?.statuses || {};
        if (signal.aborted) return;
        setExecStatusByOrg(
          Object.fromEntries(
            companies.map((row) => {
              const st = byName[row.company_name] || { ready: false, num_members: 0 };
              return [
                row._id,
                {
                  loading: false,
                  ready: Boolean(st.ready),
                  num_members: st.num_members || 0,
                },
              ];
            })
          )
        );
      } catch (err) {
        if (isAbortError(err)) return;
        setExecStatusByOrg(
          Object.fromEntries(
            companies.map((row) => [row._id, { loading: false, ready: false, num_members: 0 }])
          )
        );
      }
    })();

    return () => controller.abort();
  }, [companies]);

  const industryOptions = useMemo(() => {
    const names = new Set(companies.map((c) => c.industry).filter(Boolean));
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [companies]);

  const ownershipTypeOptions = useMemo(() => {
    const names = new Set(companies.map((c) => c.company_ownership).filter(Boolean));
    const legacy = [...names].filter((v) => !WORKSPACE_OWNERSHIP.includes(v)).sort();
    return [FILTER_ALL_OWNERSHIP, ...WORKSPACE_OWNERSHIP, ...legacy];
  }, [companies]);

  const assignedOwnerOptions = useMemo(() => {
    const byId = new Map();
    for (const row of companies) {
      const owner = row.assigned_owner;
      if (owner?.id) byId.set(owner.id, owner);
    }
    for (const admin of adminUsers) {
      if (admin.id) byId.set(admin.id, admin);
    }
    return [...byId.values()].sort((a, b) =>
      (adminDisplayName(a) || a.email || "").localeCompare(
        adminDisplayName(b) || b.email || ""
      )
    );
  }, [companies, adminUsers]);

  const filtered = useMemo(() => {
    let base = companies;
    if (filterSurvey === "active") {
      base = base.filter((row) => row.is_paused !== "Y");
    } else if (filterSurvey === "paused") {
      base = base.filter((row) => row.is_paused === "Y");
    }
    if (filterIndustry) {
      base = base.filter((row) => row.industry === filterIndustry);
    }
    if (filterOwnershipType && filterOwnershipType !== FILTER_ALL_OWNERSHIP) {
      base = base.filter((row) => row.company_ownership === filterOwnershipType);
    }
    if (filterAssignedOwner) {
      base = base.filter((row) => row.assigned_owner?.id === filterAssignedOwner);
    }
    if (filterSummary === "ready") {
      base = base.filter((row) => execStatusByOrg[row._id]?.ready === true);
    } else if (filterSummary === "not_ready") {
      base = base.filter((row) => {
        const st = execStatusByOrg[row._id];
        return st && !st.loading && !st.ready;
      });
    }
    const q = filterText.trim().toLowerCase();
    if (q) {
      base = base.filter((row) =>
        [
          row.company_name,
          row.revenue_last_year,
          row.revenue_last_12months,
          row.industry,
          row.company_size,
          row.ebita,
          row.year_of_foundation,
          row.company_ownership,
          row.invited_by?.name,
          row.invited_by?.email,
          row.assigned_owner?.name,
          row.assigned_owner?.email,
        ].some((v) => String(v || "").toLowerCase().includes(q))
      );
    }
    return sortRows(base, sortKey, sortDir);
  }, [
    companies,
    filterText,
    filterSurvey,
    filterIndustry,
    filterOwnershipType,
    filterAssignedOwner,
    filterSummary,
    sortKey,
    sortDir,
    execStatusByOrg,
  ]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice(page * pageSize, page * pageSize + pageSize);
  const pageNumbers = useMemo(() => getPageNumbers(page, pageCount), [page, pageCount]);

  const hasActiveFilters =
    !!filterText.trim() ||
    filterSurvey !== "all" ||
    !!filterIndustry ||
    filterOwnershipType !== FILTER_ALL_OWNERSHIP ||
    !!filterAssignedOwner ||
    filterSummary !== "all";

  useEffect(() => {
    if (page >= pageCount) setPage(Math.max(0, pageCount - 1));
  }, [page, pageCount]);

  const clearAllFilters = () => {
    setFilterText("");
    setFilterSurvey("all");
    setFilterIndustry("");
    setFilterOwnershipType(FILTER_ALL_OWNERSHIP);
    setFilterAssignedOwner("");
    setFilterSummary("all");
    setPage(0);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(0);
    try {
      localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(size));
    } catch {
      /* ignore */
    }
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  useEffect(() => {
    if (!setAdminAssistantContext || loading) return;
    setAdminAssistantContext(buildCompanyProfileContext(companies, filterText, execStatusByOrg));
  }, [companies, filterText, execStatusByOrg, loading, setAdminAssistantContext]);

  useEffect(() => {
    return () => setAdminAssistantContext?.(null);
  }, [setAdminAssistantContext]);

  const resetModal = () => {
    setForm(emptyForm());
    setErrors({});
    setLogoPreview(null);
    setEditOrgId("");
    setEditOwnershipHistory([]);
  };

  const requestOwnerHandover = (row, newOwnerId) => {
    const currentOwnerId = row.assigned_owner?.id || "";
    if (!newOwnerId || newOwnerId === currentOwnerId) return;
    setHandoverConfirm({
      row,
      newOwnerId,
      previousOwnerId: currentOwnerId,
    });
  };

  const confirmOwnerHandover = async () => {
    if (!handoverConfirm?.row) return;
    if (!handoverConfirm.newOwnerId) {
      toast.error("Select an admin to assign as additional ownership");
      setHandoverConfirm(null);
      return;
    }
    setHandoverBusy(true);
    try {
      await handoverCompanyOwnership({
        org_id: handoverConfirm.row._id,
        assigned_owner_id: handoverConfirm.newOwnerId,
      });
      toast.success("Other member updated");
      setHandoverConfirm(null);
      await load({ silent: true });
    } catch (e) {
      toast.error(e.message || "Could not update company owner");
    } finally {
      setHandoverBusy(false);
    }
  };

  const openAdd = () => {
    resetModal();
    setAddOpen(true);
  };

  const openEdit = (row) => {
    resetModal();
    setEditOrgId(row._id);
    setForm({
      company_name: row.company_name || "",
      year_of_foundation: row.year_of_foundation || "",
      revenue_last_year: row.revenue_last_year || "",
      revenue_last_12months: row.revenue_last_12months || "",
      industry: row.industry || "",
      company_size: row.company_size || "",
      ebita: row.ebita || "",
      company_logo: row.company_logo || "",
      company_ownership: row.company_ownership || "",
      assessment_format_id: row.assessment_format_explicit
        ? row.assessment_format_id || ""
        : "",
    });
    setEditOwnershipHistory(row.ownership_history || []);
    setLogoPreview(row.company_logo || null);
    setEditOpen(true);
  };

  const validateName = () => {
    const name = form.company_name.trim();
    const next = {};
    if (!name) next.company_name = "Company name is required.";
    else if (name !== form.company_name) {
      next.company_name = "Company name cannot have leading or trailing spaces.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleLogoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoPreview(URL.createObjectURL(file));
    setLogoUploading(true);
    try {
      const url = await uploadCompanyLogo(file);
      setForm((f) => ({ ...f, company_logo: url }));
      setLogoPreview(url);
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(err.message || "Logo upload failed");
      setLogoPreview(form.company_logo || null);
    } finally {
      setLogoUploading(false);
      e.target.value = "";
    }
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!validateName()) return;
    setSaving(true);
    try {
      const res = await createCompanyProfile({
        company_name: form.company_name.trim(),
        company_logo: form.company_logo,
        year__of_foundation: form.year_of_foundation,
        revenue_last_year: form.revenue_last_year,
        revenue_last_12months: form.revenue_last_12months,
        industry: form.industry,
        company_size: form.company_size,
        ebita: form.ebita,
        company_ownership: form.company_ownership,
        ...(form.assessment_format_id
          ? { assessment_format_id: form.assessment_format_id }
          : {}),
      });
      if (!res.data?.company) {
        toast.error(res.message || "Company with same name already exists");
        return;
      }
      toast.success("Company created successfully");
      setAddOpen(false);
      resetModal();
      await load();
      onCompanySaved?.();
    } catch (err) {
      toast.error(err.message || "Could not create company");
    } finally {
      setSaving(false);
    }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!validateName()) return;
    setSaving(true);
    try {
      await updateCompanyProfile({
        org_id: editOrgId,
        company_name: form.company_name.trim(),
        year__of_foundation: form.year_of_foundation,
        revenue_last_year: form.revenue_last_year,
        revenue_last_12months: form.revenue_last_12months,
        industry: form.industry,
        company_size: form.company_size,
        ebita: form.ebita,
        company_logo: form.company_logo,
        company_ownership: form.company_ownership,
        assessment_format_id: form.assessment_format_id || "",
      });
      toast.success("Company updated successfully");
      setEditOpen(false);
      resetModal();
      await load();
    } catch (err) {
      toast.error(err.message || "Could not update company");
    } finally {
      setSaving(false);
    }
  };

  const refreshExecSurveyStatus = useCallback(async (orgId, companyName) => {
    setExecStatusByOrg((prev) => ({
      ...prev,
      [orgId]: { ...prev[orgId], loading: true },
    }));
    try {
      const res = await fetchExecutiveSummaryStatus(companyName);
      const ready = Boolean(res.data?.ready);
      setExecStatusByOrg((prev) => ({
        ...prev,
        [orgId]: {
          loading: false,
          ready,
          num_members: res.data?.num_members || 0,
        },
      }));
      return ready;
    } catch (e) {
      setExecStatusByOrg((prev) => ({
        ...prev,
        [orgId]: { loading: false, ready: false, num_members: 0 },
      }));
      toast.error(e.message || "Could not check survey status");
      return false;
    }
  }, []);

  const openSlider = (row) => {
    setSliderRow(row);
  };

  const openSummariesModal = (row) => {
    setExecSummariesModal(row);
    const status = execStatusByOrg[row._id];
    if (!status || status.loading) {
      refreshExecSurveyStatus(row._id, row.company_name);
    }
    const defaultRoleId = EXEC_SUMMARY_ROLES[0].id;
    const defaultKey = execSummaryKey(row._id, defaultRoleId);
    if (!execPreviews[defaultKey] && !execPreviewLoading[defaultKey]) {
      loadExecPreview(row._id, row.company_name, defaultRoleId);
    }
  };

  const loadExecPreview = async (orgId, companyName, roleId) => {
    const key = execSummaryKey(orgId, roleId);
    setExecPreviewLoading((prev) => ({ ...prev, [key]: true }));
    try {
      let ready = execStatusByOrg[orgId]?.ready;
      if (ready !== true) {
        ready = await refreshExecSurveyStatus(orgId, companyName);
      }
      if (!ready) {
        toast.error(
          "No completed surveys yet — summaries are unavailable until the team finishes the assessment."
        );
        return;
      }
      const res = await previewExecutiveSummary(companyName, roleId);
      const preview = res.data?.summary || null;
      setExecPreviews((prev) => ({ ...prev, [key]: preview }));
    } catch (e) {
      toast.error(e.message || "Could not load summary preview");
    } finally {
      setExecPreviewLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const downloadExecSummaryPdf = async (orgId, companyName, roleId) => {
    const key = execSummaryKey(orgId, roleId);
    setExecPdfLoading((prev) => ({ ...prev, [key]: true }));
    try {
      let ready = execStatusByOrg[orgId]?.ready;
      if (ready !== true) {
        ready = await refreshExecSurveyStatus(orgId, companyName);
      }
      if (!ready) {
        toast.error(
          "No completed surveys yet — summaries are unavailable until the team finishes the assessment."
        );
        return;
      }

      const win = openReportLoadingTab("Generating executive summary…");

      const blob = await generateExecutiveSummaryPdf(companyName, roleId);
      const url = URL.createObjectURL(blob);
      if (win) {
        win.location.href = url;
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = `${companyName.replace(/\s+/g, "_")}_Executive_Summary_${roleId.replace(/\s+/g, "_")}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success("Executive summary ready");
    } catch (err) {
      toast.error(err?.message || "Could not generate summary");
    } finally {
      setExecPdfLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const requestPauseToggle = () => {
    if (!sliderRow) return;
    setPauseConfirmOpen(true);
  };

  const confirmPauseToggle = async () => {
    if (!sliderRow) return;
    setPauseBusy(true);
    try {
      await toggleCompanyPause([sliderRow._id]);
      toast.success("Survey status updated successfully");
      setPauseConfirmOpen(false);
      setSliderRow(null);
      await load();
    } catch (e) {
      toast.error(e.message || "Could not update survey status");
    } finally {
      setPauseBusy(false);
    }
  };

  const formatHistoryDate = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const closeEditDrawer = () => {
    setEditOpen(false);
    resetModal();
  };

  const GroupTh = ({ label, colSpan, className = "", variant = "actions" }) => {
    const variantClass =
      variant === "company"
        ? "bg-gradient-to-r from-white via-slate-50/90 to-[#004f73]/[0.04] text-slate-500 border-b border-slate-200/70"
        : variant === "profile"
          ? "bg-gradient-to-r from-white via-slate-50/90 to-slate-100/80 text-slate-500 border-b border-slate-200/70"
          : variant === "ownership"
            ? "bg-gradient-to-r from-white via-slate-50/90 to-violet-50/30 text-slate-500 border-b border-slate-200/70"
            : variant === "operations"
              ? "bg-gradient-to-r from-white via-slate-50/90 to-amber-50/35 text-slate-500 border-b border-slate-200/70"
              : "bg-slate-50/90 text-slate-400 border-b border-slate-200/80";
    return (
      <th
        colSpan={colSpan}
        className={`px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider leading-none ${variantClass} ${className}`}
      >
        {label}
      </th>
    );
  };

  const SortTh = ({ label, field }) => {
    const active = sortKey === field;
    return (
      <th
        className="px-3 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wide leading-none text-slate-500 cursor-pointer select-none whitespace-nowrap hover:text-slate-800 transition-colors border-b border-slate-200 bg-slate-50/95"
        onClick={() => toggleSort(field)}
      >
        <span className="inline-flex items-center gap-1">
          {label}
          <span className={`text-slate-400 ${active ? "text-[#004f73]" : "opacity-40"}`}>
            {active ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
          </span>
        </span>
      </th>
    );
  };

  const formFooterClass =
    "inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50";

  return (
    <div className={embedded ? "nexus-surface-embed" : "min-h-full w-full bg-white"}>
      <div className={embedded ? "w-full" : "w-full px-6 py-6"}>
        {!embedded ? (
        <header className="mb-6">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-[#004f73]/70 mb-2">
            Admin
          </p>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <h1 className="text-2xl font-bold text-gray-900">Company Profile</h1>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                className="inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors disabled:opacity-50"
                onClick={handleRefresh}
                disabled={refreshing || loading}
                title="Refresh list"
              >
                <IconRefresh className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
                Refresh
              </button>
              <button
                type="button"
                className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#004f73] hover:bg-[#003a57] rounded-lg transition-colors"
                onClick={openAdd}
              >
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4" aria-hidden>
                  <path
                    fillRule="evenodd"
                    d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                    clipRule="evenodd"
                  />
                </svg>
                Add company
              </button>
            </div>
          </div>
        </header>
        ) : (
        <div className="mb-4 flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            className="inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors disabled:opacity-50"
            onClick={handleRefresh}
            disabled={refreshing || loading}
            title="Refresh list"
          >
            <IconRefresh className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            type="button"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#004f73] hover:bg-[#003a57] rounded-lg transition-colors"
            onClick={openAdd}
          >
            Add company
          </button>
        </div>
        )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <Spinner className="h-8 w-8 text-[#004f73]" />
          <p className="text-sm text-slate-400">Loading companies…</p>
        </div>
      ) : companies.length === 0 ? (
        <div className="text-center py-20 px-6 rounded-2xl border border-dashed border-slate-200 bg-slate-50/50">
          <div className="mx-auto w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              className="w-6 h-6 text-slate-400"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z"
              />
            </svg>
          </div>
          <p className="text-base font-medium text-slate-700">No company profiles yet</p>
          <p className="text-sm text-slate-500 mt-1">
            Add your first company to start managing profiles and surveys.
          </p>
          <button
            type="button"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-[#004f73] hover:bg-[#003a57] rounded-xl shadow-sm transition-colors"
            onClick={openAdd}
          >
            Add company
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2 min-w-0">
              <p className="text-sm text-slate-500 shrink-0">
                {hasActiveFilters ? (
                  <>
                    Showing <span className="font-medium text-slate-700">{filtered.length}</span> of{" "}
                    {companies.length}
                  </>
                ) : (
                  <>
                    <span className="font-medium text-slate-700">{filtered.length}</span>{" "}
                    {filtered.length === 1 ? "company" : "companies"}
                  </>
                )}
              </p>
              <select
                value={filterSurvey}
                onChange={(e) => {
                  setFilterSurvey(e.target.value);
                  setPage(0);
                }}
                className="admin-filter-select text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#004f73]/20 min-w-[120px]"
              >
                <option value="all">All surveys</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
              </select>
              <select
                value={filterIndustry}
                onChange={(e) => {
                  setFilterIndustry(e.target.value);
                  setPage(0);
                }}
                className="admin-filter-select text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#004f73]/20 min-w-[130px]"
              >
                <option value="">All industries</option>
                {industryOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
              <select
                value={filterOwnershipType}
                onChange={(e) => {
                  setFilterOwnershipType(e.target.value);
                  setPage(0);
                }}
                className="admin-filter-select text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#004f73]/20 min-w-[140px]"
              >
                {ownershipTypeOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
              <select
                value={filterAssignedOwner}
                onChange={(e) => {
                  setFilterAssignedOwner(e.target.value);
                  setPage(0);
                }}
                className="admin-filter-select text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#004f73]/20 min-w-[130px]"
              >
                <option value="">All additional ownership</option>
                {assignedOwnerOptions.map((admin) => (
                  <option key={admin.id} value={admin.id}>
                    {adminDisplayName(admin) || admin.email}
                  </option>
                ))}
              </select>
              <select
                value={filterSummary}
                onChange={(e) => {
                  setFilterSummary(e.target.value);
                  setPage(0);
                }}
                className="admin-filter-select text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#004f73]/20 min-w-[130px]"
              >
                <option value="all">All exec summaries</option>
                <option value="ready">Ready</option>
                <option value="not_ready">Unavailable</option>
              </select>
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="text-xs font-medium text-[#004f73] hover:underline px-1 shrink-0"
                >
                  Clear filters
                </button>
              )}
            </div>
            <div className="relative w-full lg:w-72 shrink-0">
              <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                type="search"
                placeholder="Search companies…"
                value={filterText}
                onChange={(e) => {
                  setFilterText(e.target.value);
                  setPage(0);
                }}
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#004f73]/20 focus:border-[#004f73]/30"
              />
            </div>
          </div>

          <div className="admin-data-table rounded-xl border border-slate-200/80 shadow-sm overflow-hidden bg-white">
            <div className="admin-table-scroll-wrap overflow-x-auto">
              <table className="w-full text-sm min-w-[960px]">
                <thead className="sticky top-0 z-10">
                  <tr>
                    <GroupTh label="Company" colSpan={1} variant="company" />
                    <GroupTh label="Profile" colSpan={1} variant="profile" />
                    <GroupTh label="Ownership" colSpan={1} variant="ownership" />
                    <GroupTh label="Operations" colSpan={2} variant="operations" className="text-center" />
                  </tr>
                  <tr>
                    <SortTh label="Company" field="company_name" />
                    <SortTh label="Financials" field="ebita" />
                    <SortTh label="Invited by" field="invited_by" />
                    <th className="px-3 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wide leading-none text-slate-500 border-b border-slate-200 bg-slate-50/95 whitespace-nowrap min-w-[120px]">
                      Executive summary
                    </th>
                    <SortTh label="Survey" field="survey" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {pageRows.length === 0 && companies.length > 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-16 text-center">
                        <p className="text-base font-medium text-slate-700">No matches</p>
                        <p className="text-sm text-slate-500 mt-1">
                          Try adjusting your search or filters.
                        </p>
                        {hasActiveFilters && (
                          <button
                            type="button"
                            onClick={clearAllFilters}
                            className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#004f73] bg-[#004f73]/5 hover:bg-[#004f73]/10 rounded-lg transition-colors"
                          >
                            Clear filters
                          </button>
                        )}
                      </td>
                    </tr>
                  ) : null}
                  {pageRows.map((row) => (
                    <tr key={row._id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="px-3 py-2 align-middle">
                        <CompanyCell row={row} onEdit={openEdit} />
                      </td>
                      <td className="px-3 py-2 align-middle">
                        <FinancialsCell row={row} />
                      </td>
                      <td className="px-3 py-2 align-middle">
                        <OwnershipCell
                          row={row}
                          admins={adminUsers}
                          canHandover={superAdmin}
                          ownerSelectValue={row.assigned_owner?.id || ""}
                          onOwnerChange={requestOwnerHandover}
                        />
                      </td>
                      <td className="px-3 py-2 align-middle">
                        <ExecutiveSummaryPill
                          status={execStatusByOrg[row._id]}
                          onOpenSummaries={() => openSummariesModal(row)}
                        />
                      </td>
                      <td className="px-3 py-2 align-middle">
                        <SurveyStatusPill row={row} onClick={() => openSlider(row)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filtered.length > 0 && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 border-t border-slate-100 bg-slate-50/50 text-sm text-slate-600">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="tabular-nums text-xs">
                    {page * pageSize + 1}–{Math.min((page + 1) * pageSize, filtered.length)} of{" "}
                    {filtered.length}
                  </span>
                  <label className="flex items-center gap-2 text-xs">
                    <span className="text-slate-500">Rows per page</span>
                    <select
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      className="border border-slate-200 rounded-lg px-2 py-1 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#004f73]/20"
                    >
                      {PAGE_SIZE_OPTIONS.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                {pageCount > 1 && (
                  <div className="flex flex-wrap items-center gap-1">
                    <button
                      type="button"
                      className="px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                      disabled={page === 0}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      Previous
                    </button>
                    {pageNumbers.map((p, i) => {
                      const prev = pageNumbers[i - 1];
                      const showEllipsis = i > 0 && p - prev > 1;
                      return (
                        <Fragment key={p}>
                          {showEllipsis ? (
                            <span className="px-1 text-xs text-slate-400">…</span>
                          ) : null}
                          <button
                            type="button"
                            className={`min-w-[2rem] px-2 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                              page === p
                                ? "border-[#004f73] bg-[#004f73] text-white"
                                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                            }`}
                            onClick={() => setPage(p)}
                          >
                            {p + 1}
                          </button>
                        </Fragment>
                      );
                    })}
                    <button
                      type="button"
                      className="px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                      disabled={page >= pageCount - 1}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      </div>

      {addOpen ? (
        <ModalBackdrop onClose={() => { setAddOpen(false); resetModal(); }}>
          <h2 className="text-xl font-bold text-gray-900 mb-1 pr-8">Add company</h2>
          <p className="text-sm text-slate-500 mb-6">Create a new company workspace profile.</p>
          <form onSubmit={submitAdd} className="space-y-6">
            <LogoUploadBlock
              previewUrl={logoPreview}
              onPickFile={handleLogoPick}
              uploading={logoUploading}
              inputId="company-logo-input-add"
            />
            <CompanyFormFields
              form={form}
              setForm={setForm}
              errors={errors}
              assessmentFormats={assessmentFormats}
              activeFormat={activeFormat}
            />
            <div className="flex justify-end gap-3 pt-2 border-t border-slate-100">
              <button
                type="button"
                className={`${formFooterClass} text-slate-700 bg-white border border-slate-200 hover:bg-slate-50`}
                onClick={() => { setAddOpen(false); resetModal(); }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`${formFooterClass} text-white bg-[#004f73] hover:bg-[#003a57]`}
                disabled={saving}
              >
                {saving ? <Spinner className="h-4 w-4" /> : null}
                Create company
              </button>
            </div>
          </form>
        </ModalBackdrop>
      ) : null}

      {editOpen ? (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 bg-black/20 backdrop-blur-[1px]"
            aria-label="Close editor"
            onClick={closeEditDrawer}
          />
          <aside
            className="fixed top-0 right-0 z-50 h-full w-full max-w-lg bg-white shadow-2xl flex flex-col min-h-0"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="shrink-0 px-5 py-4 border-b border-slate-200 flex justify-between items-start gap-3">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-[#004f73]/70 mb-1">
                  Edit profile
                </p>
                <h2 className="text-lg font-bold text-gray-900 truncate">{form.company_name || "Company"}</h2>
              </div>
              <button
                type="button"
                className="text-slate-400 hover:text-slate-600 text-2xl leading-none shrink-0"
                onClick={closeEditDrawer}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <form onSubmit={submitEdit} className="flex flex-col flex-1 min-h-0">
              <div className="flex-1 min-h-0 overflow-y-auto px-5 py-5 space-y-6">
                <LogoUploadBlock
                  previewUrl={logoPreview}
                  onPickFile={handleLogoPick}
                  uploading={logoUploading}
                  inputId="company-logo-input-edit"
                />
                <CompanyFormFields
              form={form}
              setForm={setForm}
              errors={errors}
              assessmentFormats={assessmentFormats}
              activeFormat={activeFormat}
            />
                <section className="space-y-3 border-t border-slate-100 pt-4">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                    Ownership
                  </h3>
                  <div className="text-xs text-slate-700 space-y-2">
                    <p>
                      <span className="text-slate-400">Invited by </span>
                      {adminDisplayName(companies.find((c) => c._id === editOrgId)?.invited_by) ||
                        "Unknown"}
                    </p>
                    <p>
                      <span className="text-slate-400">Additional ownership </span>
                      {adminDisplayName(companies.find((c) => c._id === editOrgId)?.assigned_owner) ||
                        "Unassigned"}
                    </p>
                    {superAdmin ? (
                      <p className="text-slate-500 italic">
                        Use the Additional ownership dropdown in the table to hand over assignment.
                      </p>
                    ) : null}
                  </div>
                  {editOwnershipHistory.length > 0 ? (
                    <div>
                      <p className="text-sm font-semibold text-gray-800 mb-2">Handover history</p>
                      <ul className="space-y-2">
                        {editOwnershipHistory.map((entry, i) => (
                          <li key={i} className="text-sm border-b border-gray-100 pb-2">
                            <p className="font-medium text-gray-800">
                              {entry.from_name} → {entry.to_name}
                            </p>
                            <p className="text-gray-600 text-xs">
                              By {entry.by_name} · {entry.created_date}
                            </p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No ownership handover history yet.</p>
                  )}
                </section>
              </div>
              <div className="shrink-0 px-5 py-4 border-t border-slate-200 bg-slate-50/50 flex justify-end gap-3">
                <button
                  type="button"
                  className={`${formFooterClass} text-slate-700 bg-white border border-slate-200 hover:bg-slate-50`}
                  onClick={closeEditDrawer}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`${formFooterClass} text-white bg-[#004f73] hover:bg-[#003a57]`}
                  disabled={saving}
                >
                  {saving ? <Spinner className="h-4 w-4" /> : null}
                  Save changes
                </button>
              </div>
            </form>
          </aside>
        </>
      ) : null}

      {sliderRow ? (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 bg-black/20"
            aria-label="Close panel"
            onClick={() => setSliderRow(null)}
          />
          <aside
            className="fixed top-0 right-0 z-50 h-full w-full max-w-md bg-white shadow-xl flex flex-col min-h-0"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="shrink-0 p-4 border-b flex justify-between items-center gap-2">
              <p className="font-semibold text-[#004f73] truncate">{sliderRow.company_name}</p>
              <button
                type="button"
                className="text-gray-500 hover:text-gray-700 text-xl leading-none"
                onClick={() => setSliderRow(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <p className="text-lg font-semibold">Survey status</p>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={sliderRow.is_paused === "N"}
                  onChange={requestPauseToggle}
                />
                <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#004f73]" />
              </label>
            </div>
            <p className="px-4 pb-3 text-xs text-gray-500 border-b">
              {sliderRow.is_paused === "Y"
                ? "Surveys are paused for this company."
                : "Surveys are active for this company."}
            </p>
            <div className="p-4">
              <p className="text-sm font-semibold text-gray-800 mb-3">Survey history</p>
              {(sliderRow.pause_history || []).length > 0 ? (
                <ul className="space-y-3">
                  {sliderRow.pause_history.map((h, i) => (
                    <li key={i} className="text-sm border-b border-gray-100 pb-2">
                      <p className="font-medium text-gray-800">{formatHistoryDate(h.created_date)}</p>
                      <p className="text-gray-600">
                        {h.user_name || "Admin"} turned{" "}
                        {h.is_paused === "Y" ? "off" : "on"} the survey.
                      </p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500">
                  No pause history for <strong>{sliderRow.company_name}</strong>.
                </p>
              )}
            </div>
            </div>
          </aside>
        </>
      ) : null}

      {execSummariesModal ? (
        <ExecutiveSummariesModal
          key={execSummariesModal._id}
          row={execSummariesModal}
          status={execStatusByOrg[execSummariesModal._id]}
          execPreviews={execPreviews}
          execPreviewLoading={execPreviewLoading}
          execPdfLoading={execPdfLoading}
          onClose={() => setExecSummariesModal(null)}
          onPreview={(roleId) =>
            loadExecPreview(execSummariesModal._id, execSummariesModal.company_name, roleId)
          }
          onDownload={(roleId) =>
            downloadExecSummaryPdf(
              execSummariesModal._id,
              execSummariesModal.company_name,
              roleId
            )
          }
        />
      ) : null}

      {pauseConfirmOpen && sliderRow ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-600/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full text-center">
            <h3 className="text-xl font-semibold text-[#004f73] mb-2">Update survey status</h3>
            <p className="text-gray-600 text-sm mb-6">
              Are you sure you want to change the survey status for{" "}
              <strong>{sliderRow.company_name}</strong>?
            </p>
            <div className="flex justify-center gap-3">
              <button
                type="button"
                className="grey_btn text-sm"
                disabled={pauseBusy}
                onClick={() => setPauseConfirmOpen(false)}
              >
                Decline
              </button>
              <button
                type="button"
                className="blue_btn text-sm flex items-center gap-2"
                disabled={pauseBusy}
                onClick={confirmPauseToggle}
              >
                {pauseBusy ? <Spinner className="h-4 w-4" /> : null}
                Proceed
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {handoverConfirm ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-600/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full text-center">
            <h3 className="text-xl font-semibold text-[#004f73] mb-2">Assign additional ownership</h3>
            <p className="text-gray-600 text-sm mb-6">
              Assign <strong>{handoverConfirm.row.company_name}</strong> to{" "}
              <strong>
                {adminDisplayName(
                  adminUsers.find((a) => a.id === handoverConfirm.newOwnerId)
                ) || "selected admin"}
              </strong>
              ?
            </p>
            <div className="flex justify-center gap-3">
              <button
                type="button"
                className="grey_btn text-sm"
                disabled={handoverBusy}
                onClick={() => setHandoverConfirm(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="blue_btn text-sm flex items-center gap-2"
                disabled={handoverBusy}
                onClick={confirmOwnerHandover}
              >
                {handoverBusy ? <Spinner className="h-4 w-4" /> : null}
                Confirm handover
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
